import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { router } from '@inertiajs/react'
import type { Errors } from '@inertiajs/core'

interface UsePartialPollOptions {
  onStart?: () => void
  onFinish?: () => void
  onError?: (errors: Errors) => void
  onSuccess?: () => void
  pauseWhenBrowserInactive?: boolean
}

export const usePartialPoll = (
  interval: number,
  partialReloadKeys: string[],
  options: UsePartialPollOptions = {}
) => {
  const {
    onStart,
    onFinish,
    onError,
    onSuccess,
    pauseWhenBrowserInactive = true,
  } = options

  const [isVisible, setIsVisible] = useState(true)

  const stablePartialReloadKeys = useMemo(() => partialReloadKeys, [JSON.stringify(partialReloadKeys)])

  const onStartRef = useRef(onStart)
  const onFinishRef = useRef(onFinish)
  const onErrorRef = useRef(onError)
  const onSuccessRef = useRef(onSuccess)

  useEffect(() => {
    onStartRef.current = onStart
  }, [onStart])

  useEffect(() => {
    onFinishRef.current = onFinish
  }, [onFinish])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  useEffect(() => {
    if (!pauseWhenBrowserInactive) return

    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [pauseWhenBrowserInactive])

  const performPoll = useCallback(() => {

    onStartRef.current?.()

    router.reload({
      only: stablePartialReloadKeys,
      onFinish: () => {
        onFinishRef.current?.()
      },
      onSuccess: () => {
        onSuccessRef.current?.()
      },
      onError: (errors) => {
        onErrorRef.current?.(errors)
      }
    })
  }, [interval, stablePartialReloadKeys])

  useEffect(() => {
    if (pauseWhenBrowserInactive && !isVisible) return

    const pollInterval = setInterval(performPoll, interval)

    return () => clearInterval(pollInterval)
  }, [interval, isVisible, pauseWhenBrowserInactive, performPoll])
}